import { useCallback, useState } from "react";
import usePageViewTracking from "@entrilia/shared/components/previewFile/pdfViewer/usePageViewTracking";
import useEventListener from "@entrilia/shared/hooks/useEventListener";
import useIsUserActive from "@entrilia/shared/hooks/useIsUserActive";
import useResizeObserver from "@entrilia/shared/hooks/useResizeObserver";
import { arraysHaveSameItems } from "@entrilia/shared/utilities/arrayHelper";
import { ARTICLE_READ_BUCKETS_COUNT, getVisibleBuckets } from "./articleReadBuckets";

interface Props {
  sessionId: string;
  scrollContainer: HTMLElement | null;
  pageContent: HTMLElement | null;
  articleBody: HTMLElement | null;
}

const NewsArticleReadTracker = ({ sessionId, scrollContainer, pageContent, articleBody }: Props) => {
  const [visibleBuckets, setVisibleBuckets] = useState<number[]>([]);
  const isUserActive = useIsUserActive();

  const updateVisibleBuckets = useCallback(() => {
    if (!scrollContainer || !articleBody) {
      return;
    }

    const next = getVisibleBuckets(articleBody.getBoundingClientRect(), scrollContainer.getBoundingClientRect());
    setVisibleBuckets((prev) => (arraysHaveSameItems(prev, next) ? prev : next));
  }, [scrollContainer, articleBody]);

  useEventListener(scrollContainer, "scroll", updateVisibleBuckets);
  useEventListener(window, "resize", updateVisibleBuckets);
  useResizeObserver(articleBody, undefined, updateVisibleBuckets);
  // The hero image loads late and pushes the body down without resizing it; the page content grows instead
  useResizeObserver(pageContent, undefined, updateVisibleBuckets);

  usePageViewTracking({
    sessionId,
    numPages: ARTICLE_READ_BUCKETS_COUNT,
    visiblePages: visibleBuckets,
    pauseTracking: !isUserActive,
  });

  return null;
};

export default NewsArticleReadTracker;
